"use client";

import {
  Home,
  User,
  Briefcase,
  Code2,
  Mail,
  Github,
  Linkedin,
  Smartphone,
  LayoutGrid,
  Rocket,
  BookOpen,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "next-themes";
import { SEO_CONFIG } from "@/lib/seo-config";

const navItems = [
  { name: "Home", href: "/", icon: Home },
  { name: "About", href: "/about", icon: User },
  { name: "Contact", href: "/contact", icon: Mail },
];

const portals = [
  { name: "Resume", href: "/resume", icon: FileText },
  { name: "Projects", href: "/projects", icon: Briefcase },
  { name: "Blog", href: "/blogs", icon: BookOpen },
];

const socialLinks = [
  {
    name: "GitHub",
    href: "https://github.com/maisachinsharmahu",
    icon: Github,
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/sachinsharmaofficial/",
    icon: Linkedin,
  },
  {
    name: "X",
    href: "https://x.com/sachinsharmadev",
    icon: () => (
      <svg
        viewBox="0 0 24 24"
        className="w-4 h-4 fill-current"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

// Shared hover/tap classes replacing framer-motion's whileHover/whileTap -
// framer-motion was contributing real weight to the Cloudflare Worker's
// server bundle (needed eagerly for SSR regardless of client-side laziness
// tricks like LazyMotion), and the navbar renders on every page, so its
// usage was part of the fixed baseline on every request. Plain CSS
// transitions produce the same visual effect with zero bundle cost.
const hoverTapClasses =
  "transition-transform duration-150 hover:scale-[1.15] active:scale-90";

export const Navbar = () => {
  const pathname = usePathname();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Check if we're on blog subdomain
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      setIsBlogSubdomain(
        hostname.startsWith("blogs.") || hostname.includes(".blogs.")
      );
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: "-20% 0px -20% 0px" }
    );

    document.querySelectorAll("section[id]").forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const isAdminPage = pathname.startsWith("/sachinsharma/official/adminpanel");
  const isSubPage =
    pathname === "/resume" ||
    pathname === "/projects" ||
    pathname === "/journey" ||
    pathname.startsWith("/blogs") ||
    pathname.startsWith("/hire") ||
    pathname.startsWith("/best") ||
    pathname.startsWith("/faq") ||
    pathname.startsWith("/tools") ||
    pathname.startsWith("/guides") ||
    pathname.startsWith("/glossary");
  const isBlogPage = pathname.startsWith("/blogs") || isBlogSubdomain;
  const isProjectsPage = pathname === "/projects";

  const [view, setView] = useState<"projects" | "apps">("apps");

  useEffect(() => {
    const handleViewToggle = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail !== view) {
        setView(customEvent.detail as "projects" | "apps");
      }
    };

    window.addEventListener("toggleProjectsView", handleViewToggle);
    return () =>
      window.removeEventListener("toggleProjectsView", handleViewToggle);
  }, [view]);

  const handleToggleView = (newView: "projects" | "apps") => {
    if (newView !== view) {
      setView(newView);
      window.dispatchEvent(
        new CustomEvent("toggleProjectsView", { detail: newView })
      );
    }
  };

  const logoSrc = mounted
    ? resolvedTheme === "dark"
      ? "/logo-dark.webp"
      : "/logo-light.webp"
    : "/logo-dark.webp";

  if (isAdminPage) return null;

  // Hide entire navbar on blog pages - they have their own header
  if (isBlogPage) return null;

  // If on sub-pages, show simplified navbar
  if (isSubPage) {
    const subPageItems = [
      { name: "Home", href: "/", icon: Home },
      { name: "About", href: "/about", icon: User },
      { name: "Contact", href: "/contact", icon: Mail },
      { name: "Resume", href: "/resume", icon: FileText },
      { name: "Projects", href: "/projects", icon: Briefcase },
      { name: "Blog", href: "/blogs", icon: BookOpen },
    ];

    return (
      <>
        {/* Fixed Mobile Header with Background - Hide on /projects and blog */}
        {!isProjectsPage && !isBlogPage && (
          <div className="fixed top-0 left-0 right-0 z-40 md:hidden global-navbar-component">
            <div className="relative h-16 bg-background/95 backdrop-blur-md border-b border-border/50 flex items-center px-4">
              <Link href="/">
                <div className="animate-in fade-in slide-in-from-left-5 duration-500">
                  <Image
                    src={logoSrc}
                    alt="Logo"
                    width={158}
                    height={48}
                    className="h-12 w-auto"
                    priority
                  />
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Spacer for mobile header - Only on non-projects and non-blog pages */}
        {!isProjectsPage && !isBlogPage && <div className="h-16 md:hidden" />}

        {/* Fixed Desktop Logo - Hide on /projects and blog */}
        {!isProjectsPage && !isBlogPage && (
          <div className="fixed top-6 left-6 z-40 hidden md:block global-navbar-component">
            <Link href="/">
              <div className="animate-in fade-in slide-in-from-left-5 duration-500">
                <Image
                  src={logoSrc}
                  alt="Logo"
                  width={264}
                  height={80}
                  className="h-20 w-auto"
                  priority
                />
              </div>
            </Link>
          </div>
        )}
        {!isBlogPage && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-fit max-w-[95vw] global-navbar-component">
            <div
              className="animate-in fade-in slide-in-from-bottom-8 duration-700 flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-background/80 backdrop-blur-lg border border-border shadow-lg overflow-x-auto md:overflow-visible scrollbar-hide"
            >
              {/* Projects Toggle FIRST on Projects Page (Both Mobile & Desktop) */}
              {isProjectsPage && (
                <>
                  <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-full ml-1">
                    <button
                      onClick={() => handleToggleView("apps")}
                      className={cn(
                        "p-1.5 rounded-full transition-all relative group",
                        view === "apps"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      aria-label="Apps View"
                    >
                      <Smartphone className="w-3.5 h-3.5" />
                      {!isMobile && (
                        <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                          Apps View
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleView("projects")}
                      className={cn(
                        "p-1.5 rounded-full transition-all relative group",
                        view === "projects"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      aria-label="Grid View"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      {!isMobile && (
                        <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                          Grid View
                        </span>
                      )}
                    </button>
                  </div>
                  <div className="w-px h-4 bg-border mx-1" />
                </>
              )}

              {subPageItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;

                return (
                  <Link key={item.name} href={item.href} legacyBehavior>
                    <a
                      className={cn(
                        hoverTapClasses,
                        "p-2 md:p-3 rounded-full hover:bg-secondary relative group",
                        isActive
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground"
                      )}
                      aria-label={`View ${item.name}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                        {item.name}
                      </span>
                    </a>
                  </Link>
                );
              })}

              <div className="w-px h-4 bg-border mx-0.5 md:mx-1" />

              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      hoverTapClasses,
                      "p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary relative group flex"
                    )}
                    aria-label={`Visit my ${link.name}`}
                  >
                    {link.name === "X" ? (
                      <Icon className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                    <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                      {link.name}
                    </span>
                  </a>
                );
              })}

              <div className="w-px h-4 bg-border mx-0.5 md:mx-1" />
              <ThemeToggle />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Fixed Mobile Header with Background */}
      <div className="fixed top-0 left-0 right-0 z-40 md:hidden global-navbar-component">
        <div className="relative h-16 bg-background/95 backdrop-blur-md border-b border-border/50 flex items-center px-4">
          <Link href="/">
            <div className="animate-in fade-in slide-in-from-left-5 duration-500">
              <Image
                src={logoSrc}
                alt="Logo"
                width={158}
                height={48}
                className="h-12 w-auto"
                priority
              />
            </div>
          </Link>
        </div>
      </div>
      {/* Spacer for mobile header */}
      <div className="h-16 md:hidden" />
      {/* Fixed Desktop Logo - Hide on /projects */}
      {!isProjectsPage && (
        <div className="fixed top-6 left-6 z-40 hidden md:block global-navbar-component">
          <Link href="/">
            <div className="animate-in fade-in slide-in-from-left-5 duration-500">
              <Image
                src={logoSrc}
                alt="Logo"
                width={264}
                height={80}
                className="h-20 w-auto"
                priority
              />
            </div>
          </Link>
        </div>
      )}
      {!isBlogPage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-fit max-w-[98vw] global-navbar-component">
          <div
            className="animate-in fade-in slide-in-from-bottom-8 duration-700 flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-background/80 backdrop-blur-lg border border-border/50 shadow-2xl origin-center overflow-x-auto md:overflow-visible scrollbar-hide transition-transform hover:scale-x-[1.05]"
          >
            {/* Conditional Mobile Navigation Items */}
            {isMobile ? (
              <>
                {[
                  { name: "Home", href: "/", icon: Home },
                  { name: "About", href: "/about", icon: User },
                  { name: "Contact", href: "/contact", icon: Mail },
                  { name: "Resume", href: "/resume", icon: FileText },
                  { name: "Projects", href: "/projects", icon: Briefcase },
                  { name: "Blog", href: "/blogs", icon: BookOpen },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.name} href={item.href} legacyBehavior>
                      <a
                        className={cn(
                          hoverTapClasses,
                          "p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary"
                        )}
                        aria-label={`View ${item.name}`}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    </Link>
                  );
                })}
              </>
            ) : (
              /* Desktop Navigation Items (Sections) */
              navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.href.replace("#", "");

                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={cn(
                      hoverTapClasses,
                      "p-2 md:p-3 rounded-full hover:bg-secondary relative group",
                      isActive
                        ? "bg-secondary text-foreground"
                        : "text-muted-foreground"
                    )}
                    aria-label={`Go to ${item.name} section`}
                  >
                    <Icon className="w-5 h-5 inline-block" />
                    <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                      {item.name}
                    </span>
                  </a>
                );
              })
            )}

            {/* Pages / Portals Divider (Desktop only as mobile is simplified) */}
            {!isMobile && (
              <>
                <div className="flex items-center gap-1 mx-1">
                  <div className="w-px h-3.5 bg-border/60" />
                  <span className="text-[7px] font-black text-muted-foreground/40 uppercase tracking-[0.15em] hidden md:inline">
                    Pages
                  </span>
                </div>

                {portals.map((portal) => {
                  const Icon = portal.icon;
                  const isActive = pathname === portal.href;
                  const isHiddenOnMobile = portal.name === "Resume";

                  return (
                    <Link key={portal.name} href={portal.href} legacyBehavior>
                      <a
                        className={cn(
                          hoverTapClasses,
                          "p-2 md:p-3 rounded-full hover:bg-secondary relative group",
                          isActive
                            ? "bg-secondary text-foreground"
                            : "text-muted-foreground",
                          isHiddenOnMobile && "hidden md:flex"
                        )}
                        aria-label={`Open ${portal.name}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                          {portal.name}
                        </span>
                      </a>
                    </Link>
                  );
                })}
              </>
            )}

            <div className="w-px h-3.5 bg-border/60 mx-1 md:block" />

            {socialLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    hoverTapClasses,
                    "p-2 md:p-3 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary relative group flex"
                  )}
                  aria-label={`Visit my ${link.name}`}
                >
                  {link.name === "X" ? (
                    <Icon className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                  <span className="absolute -top-12 left-1/2 -translate-x-1/2 px-3.5 py-2 bg-foreground text-background text-xs font-normal rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[60] shadow-xl pointer-events-none">
                    {link.name}
                  </span>
                </a>
              );
            })}

            <div className="w-px h-3.5 bg-border/60 mx-0.5" />
            <ThemeToggle />
          </div>
        </div>
      )}
    </>
  );
};
